/**
 * Primitive validators â€” reusable building blocks for composite schemas.
 *
 * Design principles:
 * - Server-authoritative: everything here will be enforced on the API, even if
 *   the client-side also runs it. Don't weaken to be "nicer" for UX.
 * - Sanitize as part of validation (trim, lowercase) so downstream code
 *   never deals with surprise whitespace or case variants.
 * - Reject MORE than RFC strictly requires when the stricter form prevents
 *   practical abuse (e.g. email length cap, phone length cap).
 */

import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js/min";

// -----------------------------------------------------------
// Email
// -----------------------------------------------------------

/**
 * RFC-practical email validator.
 * We trim and lowercase so "Foo@BAR.ro  " and "foo@bar.ro" are the same identity
 * (avoids duplicate accounts via case tricks).
 *
 * Max length 254 per RFC 5321 Â§4.5.3.1.1.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "email_too_short")
  .max(254, "email_too_long")
  .email("email_invalid");

export type Email = z.infer<typeof emailSchema>;

// -----------------------------------------------------------
// Password
// -----------------------------------------------------------

/**
 * Password rules (server-enforced):
 * - 12 to 256 characters (NIST SP 800-63B: length over complexity)
 * - At least one lowercase, one uppercase, one digit
 *   (we DON'T require special chars â€” length does the heavy lifting)
 * - No leading/trailing whitespace
 *
 * In Phase 3.4.C we'll additionally screen against known-breach passwords
 * (k-anonymity Pwned Passwords check) during registration.
 */
export const passwordSchema = z
  .string()
  .min(12, "password_too_short")
  .max(256, "password_too_long")
  .refine((v) => v === v.trim(), { message: "password_has_whitespace_edges" })
  .refine((v) => /[a-z]/.test(v), { message: "password_missing_lowercase" })
  .refine((v) => /[A-Z]/.test(v), { message: "password_missing_uppercase" })
  .refine((v) => /\d/.test(v), { message: "password_missing_digit" });

export type Password = z.infer<typeof passwordSchema>;

// -----------------------------------------------------------
// Phone (digits-only international, SMS industry standard)
// -----------------------------------------------------------

/**
 * Phone normalization: strip all non-digit characters (including leading "+"
 * if present) and validate as digits-only international format.
 *
 * Accepts inputs like "+40 721 123 456", "0040721123456", "40-721-123-456"
 * â†’ normalizes to "40721123456".
 *
 * Length: 8â€“15 digits total (E.164 Â§4 maximum of 15 digits including
 * country code; 8 is a practical minimum for any routable number).
 *
 * Rationale: SMS gateways (sendSMS, Twilio, Vonage, MessageBird, etc.) accept
 * digits-only; storing without "+" is the industry norm and keeps the field
 * trivially indexable/searchable as a plain number.
 *
 * NOTE: We do NOT strip "00" international prefixes automatically â€” "0040..."
 * would be rejected as starting with "0" (non-E.164). Forms should remove "00"
 * client-side and pass "40..." or "+40...".
 */
export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[+\s\-()]/g, ""))
  .refine((v) => /^[1-9]\d{7,14}$/.test(v), {
    message: "phone_not_e164",
  })
  .refine((v) => isValidPhoneNumber(`+${v}`), {
    message: "phone_invalid",
  });

export type Phone = z.infer<typeof phoneSchema>;

// -----------------------------------------------------------
// IANA timezone (formatting-only; shape here, EXISTENCE checked at
// runtime by a real Intl construction in api lib/timezone.ts --
// unknown zones fall back to UTC)
// -----------------------------------------------------------

export const timezoneSchema = z
  .string()
  .trim()
  .max(64)
  .regex(/^[A-Za-z0-9_+\/-]+$/, "timezone_invalid");

export type Timezone = z.infer<typeof timezoneSchema>;

/**
 * Flexible phone schema — for user-facing input forms (campaigns wizard,
 * AddContactModal, single-send forms). Permissive on shape; backend
 * normalizes to canonical E.164 digits-only via tryNormalizePhone()
 * server-side, using the user's phone_default_country preference.
 *
 * Accepts:
 *   "+40721234567"   (E.123 international notation)
 *   "40721234567"    (digits only with country code)
 *   "0721234567"     (national format — RO with default country = RO)
 *   "0040721234567"  (00-prefix international)
 *   "+40 721 234 567" (with spaces)
 *   "(0721) 234567"  (with parens)
 *
 * Mirrors contactPhoneSchema pattern (contacts.ts:87) for consistency.
 *
 * Tech-debt #30 RESOLVED: phoneSchema strict required first digit 1-9,
 * which rejected national format. flexiblePhoneSchema accepts raw input
 * + server-side normalize via tryNormalizePhone(raw, defaultCountry).
 */
export const flexiblePhoneSchema = z
  .string()
  .trim()
  .min(6, "phone_too_short")
  .max(20, "phone_too_long")
  .refine((v) => /^[+0-9\s\-()]+$/.test(v), { message: "phone_invalid_chars" });

export type FlexiblePhone = z.infer<typeof flexiblePhoneSchema>;

// -----------------------------------------------------------
// Country code (ISO 3166-1 alpha-2)
// -----------------------------------------------------------

/**
 * 2 uppercase letters. We accept lowercase input and normalize up-case.
 * This is NOT a list-check against known countries â€” we trust the frontend
 * dropdown to be authoritative, but we enforce shape here.
 */
export const countryCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(2, "country_code_invalid_length")
  .regex(/^[A-Z]{2}$/, "country_code_invalid_format");

export type CountryCode = z.infer<typeof countryCodeSchema>;

// -----------------------------------------------------------
// Romanian CUI (tax_id)
// -----------------------------------------------------------

/**
 * Romanian CUI ("Cod Unic de Identificare" fiscal).
 * Format per ANAF: optional "RO" prefix + 2â€“10 digits.
 * We normalize to "RO" + digits for consistent downstream comparison.
 *
 * VIES (VAT Information Exchange System) validation happens server-side
 * as a separate async check (needs network) during registration.
 */
export const roTaxIdSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((v) => v.replace(/[\s\-]/g, ""))
  .refine((v) => /^(RO)?\d{2,10}$/.test(v), { message: "tax_id_invalid_ro" })
  .transform((v) => (v.startsWith("RO") ? v : "RO" + v));

export type RoTaxId = z.infer<typeof roTaxIdSchema>;

/**
 * Generic EU tax_id â€” country prefix + digits/alphanumeric body.
 * Used for non-RO organizations. Full VIES validation still happens server-side.
 */
export const euTaxIdSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((v) => v.replace(/[\s\-]/g, ""))
  .refine((v) => /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(v), {
    message: "tax_id_invalid_eu",
  });

export type EuTaxId = z.infer<typeof euTaxIdSchema>;

// -----------------------------------------------------------
// Organization name
// -----------------------------------------------------------

export const organizationNameSchema = z
  .string()
  .trim()
  .min(2, "org_name_too_short")
  .max(200, "org_name_too_long");

// -----------------------------------------------------------
// Full name (first + last concatenated, one field)
// -----------------------------------------------------------

export const fullNameSchema = z
  .string()
  .trim()
  .min(2, "full_name_too_short")
  .max(200, "full_name_too_long");

// -----------------------------------------------------------
// TOTP 6-digit code
// -----------------------------------------------------------

export const totpCodeSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, ""))
  .refine((v) => /^\d{6}$/.test(v), { message: "totp_code_invalid" });

// -----------------------------------------------------------
// Backup code (10 digits, may include one dash for UX)
// -----------------------------------------------------------

export const backupCodeSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s\-]/g, ""))
  .refine((v) => /^\d{10}$/.test(v), { message: "backup_code_invalid" });

// -----------------------------------------------------------
// UUID (v7 or generic v4+) â€” used for entity IDs in path params
// -----------------------------------------------------------

export const uuidSchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    "uuid_invalid",
  );

// -----------------------------------------------------------
// CIDR (for IP allowlists)
// -----------------------------------------------------------

/**
 * IPv4 or IPv6 CIDR. We accept single addresses too, normalize to /32 or /128.
 * Full range validation (e.g. "999.999.999.999/99" rejection) done by regex.
 */
export const cidrSchema = z
  .string()
  .trim()
  .refine(
    (v) => {
      // IPv4: up to 4 octets, optional /0-32
      const v4 =
        /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\/(3[0-2]|[12]?\d))?$/;
      // IPv6: simplified match, allows full form & "::" shorthand; /0-128
      const v6 = /^[0-9a-fA-F:]+(\/(12[0-8]|1[01]\d|[1-9]?\d))?$/;
      return v4.test(v) || v6.test(v);
    },
    { message: "cidr_invalid" },
  )
  .transform((v) => {
    // If no prefix: append /32 for v4, /128 for v6
    if (!v.includes("/")) {
      if (v.includes(":")) return v + "/128";
      return v + "/32";
    }
    return v;
  });
